import React, { useEffect, useState } from "react";
import {
  listMySchools,
  listClasses,
  listStaff,
  TEACHER_DEPARTMENT,
  listTuitionBatches,
  createTuitionBatch,
  deleteTuitionBatch,
  getTuitionRows,
  setTuitionRows,
} from "./happyschoolApi";
import { getActiveSchoolId } from "./activeSchool";

// Thu học phí theo đợt: mỗi đợt là 1 bảng Lớp × (GV chủ nhiệm, Ngày thu, Tiền
// mặt, Tiền CK, Ghi chú). Có thể tạo nhiều đợt (Đợt 1, Đợt 2...) song song.

const fmtMoney = (n) => (Number(n) || 0).toLocaleString("vi-VN");

const HappySchoolTuition = () => {
  const schoolId = getActiveSchoolId();
  const [isOwnerAdmin, setIsOwnerAdmin] = useState(false);
  const [batches, setBatches] = useState([]);
  const [selectedBatchId, setSelectedBatchId] = useState(null);
  const [classes, setClasses] = useState([]);
  const [staff, setStaff] = useState([]);
  const [rows, setRows] = useState({}); // classId -> { collectionDate, cash, transfer, note }
  const [loadingBatches, setLoadingBatches] = useState(true);
  const [loadingRows, setLoadingRows] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [msg, setMsg] = useState("");
  const [showNewForm, setShowNewForm] = useState(false);
  const [newName, setNewName] = useState("");
  const [newNote, setNewNote] = useState("");
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (!schoolId) return;
    listMySchools()
      .then((l) => { const s = (l || []).find((x) => x.id === schoolId); setIsOwnerAdmin(!!s && (s.role === "owner" || s.role === "admin")); })
      .catch(() => {});
    listClasses(schoolId).then(setClasses).catch((e) => setError(e.message));
    listStaff(schoolId, { department: TEACHER_DEPARTMENT }).then(setStaff).catch((e) => setError(e.message));
    reloadBatches();
  }, [schoolId]); // eslint-disable-line

  const reloadBatches = () => {
    setLoadingBatches(true);
    listTuitionBatches(schoolId)
      .then((list) => {
        setBatches(list);
        setSelectedBatchId((cur) => cur || (list[0] && list[0].id) || null);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoadingBatches(false));
  };

  useEffect(() => {
    if (!schoolId || !selectedBatchId) return;
    setLoadingRows(true);
    setError("");
    getTuitionRows(schoolId, selectedBatchId)
      .then((r) => setRows(r || {}))
      .catch((e) => setError(e.message))
      .finally(() => setLoadingRows(false));
  }, [schoolId, selectedBatchId]);

  const staffById = Object.fromEntries(staff.map((s) => [s.id, s]));
  const sortedClasses = classes
    .slice()
    .sort((a, b) => (a.name || "").localeCompare(b.name || "", "vi", { numeric: true }));

  const createBatch = async (e) => {
    e.preventDefault();
    if (!newName.trim() || creating) return;
    setCreating(true);
    setError("");
    try {
      const b = await createTuitionBatch(schoolId, newName.trim(), newNote.trim());
      setNewName("");
      setNewNote("");
      setShowNewForm(false);
      setBatches((list) => [b, ...list]);
      setSelectedBatchId(b.id);
    } catch (err) {
      setError(err.message);
    } finally {
      setCreating(false);
    }
  };

  const removeBatch = async (batchId) => {
    if (!window.confirm("Xoá đợt thu này? Toàn bộ dữ liệu thu của đợt sẽ mất, không khôi phục được.")) return;
    setError("");
    try {
      await deleteTuitionBatch(schoolId, batchId);
      setBatches((list) => list.filter((b) => b.id !== batchId));
      setSelectedBatchId((cur) => (cur === batchId ? null : cur));
    } catch (err) {
      setError(err.message);
    }
  };

  const updateRow = (classId, field, value) => {
    setRows((r) => ({ ...r, [classId]: { ...(r[classId] || {}), [field]: value } }));
  };

  const save = async () => {
    setSaving(true);
    setError("");
    setMsg("");
    try {
      await setTuitionRows(schoolId, selectedBatchId, rows);
      setMsg("Đã lưu.");
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  let totalCash = 0;
  let totalTransfer = 0;
  sortedClasses.forEach((c) => {
    const r = rows[c.id] || {};
    totalCash += Number(r.cash) || 0;
    totalTransfer += Number(r.transfer) || 0;
  });

  if (!schoolId) return <p style={{ color: "#6b7280" }}>Chưa chọn trường.</p>;

  return (
    <div style={{ display: "flex", gap: 20, alignItems: "flex-start" }}>
      <div style={{ width: 240, flexShrink: 0 }}>
        <h4 style={{ margin: "0 0 8px" }}>Các đợt thu</h4>
        {loadingBatches ? (
          <p style={{ color: "#6b7280", fontSize: 13 }}>Đang tải...</p>
        ) : (
          <div style={{ border: "1px solid #e5e7eb", borderRadius: 8, overflow: "hidden", marginBottom: 10 }}>
            {batches.length === 0 && <div style={{ padding: 10, color: "#6b7280", fontSize: 13 }}>Chưa có đợt thu nào.</div>}
            {batches.map((b) => (
              <div
                key={b.id}
                onClick={() => setSelectedBatchId(b.id)}
                style={{
                  padding: "10px 12px",
                  cursor: "pointer",
                  borderBottom: "1px solid #f1f5f9",
                  background: selectedBatchId === b.id ? "#eff6ff" : "#fff",
                  fontWeight: selectedBatchId === b.id ? 600 : 400,
                  fontSize: 13,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <span>{b.name}</span>
                {isOwnerAdmin && (
                  <span
                    onClick={(e) => { e.stopPropagation(); removeBatch(b.id); }}
                    style={{ color: "#dc2626", fontSize: 12, marginLeft: 8 }}
                    title="Xoá đợt"
                  >
                    ✕
                  </span>
                )}
              </div>
            ))}
          </div>
        )}
        {isOwnerAdmin && !showNewForm && (
          <button type="button" onClick={() => setShowNewForm(true)} style={ghostBtn}>+ Tạo đợt mới</button>
        )}
        {isOwnerAdmin && showNewForm && (
          <form onSubmit={createBatch} style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <input placeholder="Tên đợt (vd: Đợt 1 - HK1)" value={newName} onChange={(e) => setNewName(e.target.value)} style={inputStyle} autoFocus />
            <input placeholder="Ghi chú (không bắt buộc)" value={newNote} onChange={(e) => setNewNote(e.target.value)} style={inputStyle} />
            <div style={{ display: "flex", gap: 6 }}>
              <button type="submit" disabled={creating || !newName.trim()} style={applyBtn}>{creating ? "Đang tạo..." : "Tạo"}</button>
              <button type="button" onClick={() => setShowNewForm(false)} style={ghostBtn}>Huỷ</button>
            </div>
          </form>
        )}
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        {error && <p style={{ color: "#dc2626" }}>{error}</p>}
        {msg && <p style={{ color: "#059669" }}>{msg}</p>}
        {!selectedBatchId ? (
          <p style={{ color: "#6b7280" }}>Chọn hoặc tạo một đợt thu ở bên trái.</p>
        ) : loadingRows ? (
          <p>Đang tải...</p>
        ) : (
          <>
            <div style={{ display: "flex", gap: 16, marginBottom: 12, flexWrap: "wrap" }}>
              <div style={statBox}>Tổng tiền mặt: <b>{fmtMoney(totalCash)}</b> đ</div>
              <div style={statBox}>Tổng chuyển khoản: <b>{fmtMoney(totalTransfer)}</b> đ</div>
              <div style={{ ...statBox, background: "#ecfdf5" }}>Tổng cộng: <b>{fmtMoney(totalCash + totalTransfer)}</b> đ</div>
            </div>
            <div style={{ overflowX: "auto", border: "1px solid #e5e7eb", borderRadius: 8, marginBottom: 12 }}>
              <table style={{ borderCollapse: "collapse", fontSize: 13, width: "100%" }}>
                <thead>
                  <tr>
                    <th style={th}>Lớp</th>
                    <th style={{ ...th, textAlign: "left" }}>GV chủ nhiệm</th>
                    <th style={th}>Ngày thu</th>
                    <th style={th}>Tiền mặt</th>
                    <th style={th}>Chuyển khoản</th>
                    <th style={th}>Tổng</th>
                    <th style={{ ...th, textAlign: "left" }}>Ghi chú</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedClasses.map((c) => {
                    const r = rows[c.id] || {};
                    const homeroom = c.homeroomTeacherId ? (staffById[c.homeroomTeacherId] || {}).name || c.homeroomTeacherId : "—";
                    const total = (Number(r.cash) || 0) + (Number(r.transfer) || 0);
                    return (
                      <tr key={c.id}>
                        <td style={{ ...td, fontWeight: 600 }}>{c.name}</td>
                        <td style={{ ...td, textAlign: "left" }}>{homeroom}</td>
                        <td style={td}>
                          <input type="date" value={r.collectionDate || ""} onChange={(e) => updateRow(c.id, "collectionDate", e.target.value)} style={cellInput} />
                        </td>
                        <td style={td}>
                          <input type="number" min={0} value={r.cash ?? ""} onChange={(e) => updateRow(c.id, "cash", e.target.value)} style={{ ...cellInput, width: 90, textAlign: "right" }} />
                        </td>
                        <td style={td}>
                          <input type="number" min={0} value={r.transfer ?? ""} onChange={(e) => updateRow(c.id, "transfer", e.target.value)} style={{ ...cellInput, width: 90, textAlign: "right" }} />
                        </td>
                        <td style={{ ...td, fontWeight: 600 }}>{fmtMoney(total)}</td>
                        <td style={td}>
                          <input value={r.note || ""} onChange={(e) => updateRow(c.id, "note", e.target.value)} style={{ ...cellInput, width: 160 }} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <button type="button" onClick={save} disabled={saving} style={applyBtn}>{saving ? "Đang lưu..." : "💾 Lưu"}</button>
          </>
        )}
      </div>
    </div>
  );
};

const statBox = { padding: "8px 14px", border: "1px solid #e5e7eb", borderRadius: 8, background: "#f9fafb", fontSize: 13 };
const th = { border: "1px solid #e5e7eb", padding: "6px 8px", background: "#f9fafb", whiteSpace: "nowrap", textAlign: "center" };
const td = { border: "1px solid #e5e7eb", padding: "6px 8px", textAlign: "center" };
const cellInput = { padding: 5, border: "1px solid #d1d5db", borderRadius: 6, fontSize: 13, width: 120 };
const inputStyle = { padding: 8, border: "1px solid #d1d5db", borderRadius: 6, fontSize: 13 };
const applyBtn = { padding: "10px 18px", background: "#0ea5a3", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", fontWeight: 600, fontSize: 14 };
const ghostBtn = { padding: "8px 14px", background: "#fff", color: "#374151", border: "1px solid #d1d5db", borderRadius: 6, cursor: "pointer", fontWeight: 600, fontSize: 13 };

export default HappySchoolTuition;
